import { useState, useCallback } from 'react';
import { getApiHeaders, throwIfAuthErrorResponse } from '../utils/api';
import { getQuestionnaireAnswersStreamUrl } from '../utils/questionnaireApi';
import { getAccessToken } from '../../../shared/auth/okta';

export interface RetrievalSource {
  source_display_name: string;
  relevance_score: number;
  text_excerpt: string;
}

// Answer interface matching backend response
export interface Answer {
  answer: string;
  compliance_status?: 'Yes' | 'No' | 'NA';
  id: string;
  question_number?: string;
  question: string;
  question_locale?: string | null;
  full_question_english?: string | null;
  relevance_score: number;
  retrieval_sources?: RetrievalSource[] | null;
  user_edited_ans?: string | null;
  qaf_id?: string;  // QAF ID for feedback - received via separate qaf_ids event
  session_uuid?: string;
  feedback_rating?: string | null;
  feedback_reason?: string | null;
}

// Progress state interface
export interface ProgressState {
  processed: number;
  total: number;
  percentage?: number;
  batch?: number;
  totalBatches?: number;
}

// Status types from backend
export type StreamingStatus = 
  | 'idle' 
  | 'loading' 
  | 'parsing' 
  | 'processing' 
  | 'completed' 
  | 'error';

// SSE Event types
interface SSEStatusEvent {
  event: 'status';
  status: StreamingStatus;
  message?: string;
}

interface SSEProgressEvent {
  event: 'progress';
  processed: number;
  total: number;
  percentage: number;
  batch: number;
  total_batches: number;
}

interface SSEAnswersEvent {
  event: 'answers';
  answers: Answer[];
  processed: number;
  total: number;
}

interface SSEBatchErrorEvent {
  event: 'batch_error';
  batch: number;
  error: string;
  questions_affected: number;
}

interface SSECompleteEvent {
  event: 'complete';
  session_uuid: string;
  total_processed: number;
  total_questions: number;
}

interface SSEErrorEvent {
  event: 'error';
  message: string;
  code?: string;
  status?: string;
}

// Error codes from backend
export type ErrorCode = 'MAX_QUESTIONS_EXCEEDED' | string;

// QAF IDs event - maps question IDs to QAF IDs for feedback
interface SSEQafIdsEvent {
  event: 'qaf_ids';
  qaf_mapping: Record<string, string>;  // { questionId: qafId }
  session_uuid: string;
  full_question_english?: Record<string, string>;
}

// Metadata for the request
export interface QuestionMetadata {
  client_name?: string | null;
  engagement_type?: string | null;
  laptop_type?: string | null;
  country_office?: string | null;
  market?: string | null;
  contract_type?: string | null;
  regulatory_constraints?: string | null;
  file_uuid?: string | null;
}

// Structured question for file-based flow (preserves position for writing answers back)
export interface StructuredQuestion {
  sheet_name: string | null;
  row_index: number | null;
  question_id?: string;
  question_number: string;
  question_text: string;
  question_locale?: string | null;
}

// Callback handlers interface
interface StreamCallbacks {
  onStatus?: (data: SSEStatusEvent) => void;
  onProgress?: (data: SSEProgressEvent) => void;
  onAnswers?: (data: SSEAnswersEvent) => void;
  onBatchError?: (data: SSEBatchErrorEvent) => void;
  onComplete?: (data: SSECompleteEvent) => void;
  onError?: (data: SSEErrorEvent) => void;
  onQafIds?: (data: SSEQafIdsEvent) => void;
}

/**
 * Streams answers from the backend using Server-Sent Events
 * @param questions - Plain text questions (for direct flow)
 * @param metadata - Request metadata
 * @param callbacks - SSE event callbacks
 * @param structuredQuestions - Structured questions array (for file upload flow)
 */
export async function getAnswersStream(
  questions: string | null,
  metadata: QuestionMetadata,
  callbacks: StreamCallbacks,
  structuredQuestions?: StructuredQuestion[]
): Promise<{ success: boolean; error?: string }> {
  try {
    const accessToken = getAccessToken();
    const headers = getApiHeaders(accessToken);

    // Build request body based on whether we have structured questions
    const requestBody: Record<string, unknown> = { metadata };
    
    if (structuredQuestions && structuredQuestions.length > 0) {
      // File upload flow: use structured questions (preserves sheet_name/row_index)
      requestBody.structured_questions = structuredQuestions;
      if (metadata.file_uuid) {
        requestBody.file_uuid = metadata.file_uuid;
      }
    } else if (questions) {
      // Direct flow: use plain text questions
      requestBody.questions = questions;
    }

    const response = await fetch(getQuestionnaireAnswersStreamUrl(), {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    throwIfAuthErrorResponse(response);

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventType: string | null = null;  // Track event type from 'event:' line

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      
      // Keep the last incomplete line in the buffer
      buffer = lines.pop() || '';

      for (const line of lines) {
        // Handle 'event:' line (standard SSE format)
        if (line.startsWith('event:')) {
          currentEventType = line.slice(6).trim();
          continue;
        }

        // Handle 'data:' line
        if (line.startsWith('data:')) {
          try {
            const jsonStr = line.slice(5).trim();
            if (!jsonStr) continue;  // Skip empty data lines
            
            const eventData = JSON.parse(jsonStr);
            
            // Determine event type: use 'event:' line if available, or 'event' field in JSON
            const eventType = currentEventType || eventData.event;
            
            // Route events to appropriate callbacks
            switch (eventType) {
              case 'status':
                callbacks.onStatus?.({ event: 'status', ...eventData });
                break;
              case 'progress':
                callbacks.onProgress?.({ event: 'progress', ...eventData });
                break;
              case 'answers':
                callbacks.onAnswers?.({ event: 'answers', ...eventData });
                break;
              case 'batch_error':
                callbacks.onBatchError?.({ event: 'batch_error', ...eventData });
                break;
              case 'complete':
                callbacks.onComplete?.({ event: 'complete', ...eventData });
                break;
              case 'error':
                callbacks.onError?.({ event: 'error', ...eventData });
                break;
              case 'qaf_ids':
                callbacks.onQafIds?.({ event: 'qaf_ids', ...eventData });
                break;
              default:
                // Handle legacy events without 'event' field for backwards compatibility
                handleLegacyEvent(eventData, callbacks);
            }
            
            // Reset event type after processing data
            currentEventType = null;
          } catch (parseError) {
            console.error('Error parsing SSE event:', parseError, 'Line:', line);
          }
        }
      }
    }

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    callbacks.onError?.({ event: 'error', message: errorMessage });
    return { success: false, error: errorMessage };
  }
}

/**
 * Handle legacy SSE events that don't have an 'event' field
 */
function handleLegacyEvent(data: any, callbacks: StreamCallbacks) {
  // Legacy format handling
  if (data.answers && Array.isArray(data.answers)) {
    callbacks.onAnswers?.({
      event: 'answers',
      answers: data.answers,
      processed: data.processed || 0,
      total: data.total || 0,
    });
  }

  if (data.processed !== undefined && data.total !== undefined && !data.answers) {
    callbacks.onProgress?.({
      event: 'progress',
      processed: data.processed,
      total: data.total,
      percentage: data.percentage || Math.round((data.processed / data.total) * 100),
      batch: data.batch || 0,
      total_batches: data.total_batches || 1,
    });
  }

  if (data.status === 'completed') {
    callbacks.onComplete?.({
      event: 'complete',
      session_uuid: data.session_uuid || '',
      total_processed: data.processed || 0,
      total_questions: data.total || 0,
    });
  }

  if (data.status && data.status !== 'completed') {
    callbacks.onStatus?.({
      event: 'status',
      status: data.status,
      message: data.message,
    });
  }
}

/**
 * Custom hook for streaming answers with full state management
 */
export function useStreamingAnswers() {
  const [status, setStatus] = useState<StreamingStatus>('idle');
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [progress, setProgress] = useState<ProgressState>({ processed: 0, total: 0 });
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<ErrorCode | null>(null);
  const [sessionUuid, setSessionUuid] = useState<string | null>(null);
  const [batchErrors, setBatchErrors] = useState<SSEBatchErrorEvent[]>([]);
  const [qafIdsReceived, setQafIdsReceived] = useState<boolean>(false);

  const resetState = useCallback(() => {
    setStatus('idle');
    setStatusMessage('');
    setProgress({ processed: 0, total: 0 });
    setAnswers([]);
    setError(null);
    setErrorCode(null);
    setSessionUuid(null);
    setBatchErrors([]);
    setQafIdsReceived(false);
  }, []);

  const submitQuestions = useCallback(async (
    questions: string | null,
    metadata: QuestionMetadata,
    structuredQuestions?: StructuredQuestion[]
  ) => {
    // Reset state before starting
    setStatus('loading');
    setStatusMessage('Initializing...');
    setAnswers([]);
    setError(null);
    setErrorCode(null);
    setProgress({ processed: 0, total: 0 });
    setSessionUuid(null);
    setBatchErrors([]);
    setQafIdsReceived(false);

    const result = await getAnswersStream(questions, metadata, {
      onStatus: (data) => {
        setStatus(data.status);
        if (data.message) {
          setStatusMessage(data.message);
        } else {
          // Set default messages based on status
          switch (data.status) {
            case 'loading':
              setStatusMessage('Loading...');
              break;
            case 'parsing':
              setStatusMessage('Parsing questions...');
              break;
            case 'processing':
              setStatusMessage('Processing questions...');
              break;
            case 'completed':
              setStatusMessage('Completed!');
              break;
            case 'error':
              setStatusMessage('An error occurred');
              break;
            default:
              setStatusMessage('');
          }
        }
      },
      onProgress: (data) => {
        setProgress({
          processed: data.processed,
          total: data.total,
          percentage: data.percentage,
          batch: data.batch,
          totalBatches: data.total_batches,
        });
      },
      onAnswers: (data) => {
        setAnswers(prev => [...prev, ...data.answers]);
        setProgress(prev => ({
          ...prev,
          processed: data.processed,
          total: data.total,
        }));
      },
      onBatchError: (data) => {
        console.warn('Batch error:', data);
        setBatchErrors(prev => [...prev, data]);
      },
      onComplete: (data) => {
        setStatus('completed');
        setSessionUuid(data.session_uuid);
        setStatusMessage('All questions processed successfully!');
      },
      onError: (data) => {
        setStatus('error');
        setErrorCode(data.code || null);
        
        // Handle specific error codes with user-friendly messages
        let errorMessage = data.message;
        if (data.code === 'MAX_QUESTIONS_EXCEEDED') {
          errorMessage = data.message || 'Too many questions. Please reduce the number of questions and try again.';
        }
        
        setError(errorMessage);
        setStatusMessage(errorMessage);
      },
      onQafIds: (data) => {
        const englishById = data.full_question_english ?? {};
        setAnswers(prev =>
          prev.map(answer => {
            const qafId = data.qaf_mapping[answer.id];
            const en = englishById[answer.id];
            if (qafId || en) {
              return {
                ...answer,
                ...(qafId ? { qaf_id: qafId } : {}),
                ...(en ? { full_question_english: en } : {}),
              };
            }
            return answer;
          })
        );
        // Update session UUID if provided
        if (data.session_uuid) {
          setSessionUuid(data.session_uuid);
        }
        setQafIdsReceived(true);
        console.log('QAF IDs received, feedback now available');
      },
    }, structuredQuestions);

    return result;
  }, []);

  return {
    // State
    status,
    statusMessage,
    progress,
    answers,
    error,
    errorCode,
    sessionUuid,
    batchErrors,
    qafIdsReceived,
    
    // Actions
    submitQuestions,
    resetState,
    
    // Computed states
    isLoading: status === 'loading' || status === 'processing' || status === 'parsing',
    isComplete: status === 'completed',
    hasError: status === 'error',
    hasAnswers: answers.length > 0,
    canProvideFeedback: qafIdsReceived,  // True when qaf_ids are available for feedback
    isMaxQuestionsExceeded: errorCode === 'MAX_QUESTIONS_EXCEEDED',
  };
}

export default useStreamingAnswers;

